import {useAuthInfo, WithLoggedInAuthInfoProps, withRequiredAuthInfo} from "@propelauth/react";
import {useParams} from "react-router-dom";
import {deleteWithToken, fetchWithToken, patchWithToken, postWithToken} from "../../api/apiBase";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {DataTable} from "../ui/dataTable";
import {ColumnDef} from "@tanstack/react-table";
import {Button} from "../ui/button";
import React, {useContext, useEffect, useState} from "react";
import {ProjectMeta} from "@models/project";
import {
    PermissionTypeEnum,
    PermissionedDirectoryEntryRef,
    PermissionMeta,
    CompanyPermissionDesriptions
} from "../../models/permissions";
import {Switch} from "../ui/switch";
import {UserContext} from "../../contexts/UserContext";
import {useToast} from "../ui/use-toast";
import {CompanyType} from "../../models/company";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select"
import {Separator} from '../ui/separator'
import UserProfile from "../admin/Profile";


interface UserManagementView {
    user: any,
    projects: ProjectMeta[]
    user_projects: PermissionedDirectoryEntryRef[]

}

type PermissionDropDownProps = {
    row: any,
    key_str: string,
    max: number,
    onChange: (project_id: string, index: number, permission: string, value: number) => void

}

/**
 *
 * todo - note to self - some of this needs to exposed to builder as well -
 *      they must be able to invite team mates and such. .... be VERY CAREFUL
 * - add COMPANY-LEVEL permissions to here for each user
 * - filter 'MAX ASSIGNABLE PERMISSIONS ' for external users: (here AND in endpoint ...)
 * - @classmethod
 *     def new_bidder_invited(cls):
 *         return cls(
 *             view_project=True,
 *             edit_project=False,
 *             admin_project=False,
 *             project=2,       -k      PermissionEnum.view
 *             rfp_prequal=4, -tbd      PermissionEnum.edit
 *             bidding=4, -k            PermissionEnum.edit --- havent decided if there is anotehr lvell
 *             value_engineering=4, -k  PermissionEnum.edit
 *             directory=2, -k          PermissionEnum.view... - todo something here im forgetting
 *         )
 *
 * */
function PermissionDropdown({row, key_str, max, onChange}: PermissionDropDownProps) {

    return <Select
        onValueChange={(value) => {
            onChange(row.original.project_meta.id, row.index, key_str, Number(value))
        }}
        defaultValue={row.original[key_str]}
    >
        <SelectTrigger>
            {PermissionTypeEnum[row.original[key_str]]}
        </SelectTrigger>
        <SelectContent className="w-56">
            <SelectItem value="0">None</SelectItem>
            {max >= 2 ? <SelectItem value="2">View</SelectItem> : <></>}
            {max >= 4 ? <SelectItem value="4">Editor</SelectItem> : <></>}
            {max >= 8 ? <SelectItem value="8">Admin</SelectItem> : <></>}
        </SelectContent>
    </Select>
}

type UserMgmtProps = {
    data: UserManagementView;
    setData: (x: UserManagementView) => void;
}


function CompanyPermissionView({data, setData}: UserMgmtProps) {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const userContext = useContext(UserContext);

    const company_permissions = data.user.company_permissions;

    const onUpdateCompanyPermissions = (key: string, value: boolean) => {
        if (!data) {
            return;
        }

        company_permissions[key] = value
        patchWithToken(`/user/manage/${data.user.email}/company_permissions`,
            JSON.stringify(company_permissions),
            accessToken as string)
            .then((res) => {
                setData(res)

            }).catch((err) => {
            toast({
                title: "Error",
                description: "Error updating permissions: " + String(err),
                variant: "destructive",
            });
        })
    }

    return <div>
        <span>Company Permissions</span>
        <div className="w-full  justify-between">
            {CompanyPermissionDesriptions.map((perm: PermissionMeta) => {
                    if (perm.access && perm.access !== userContext?.company_type) {
                        return <></>
                    }
                    return <div className="w-full columns-2 justify-between">

                        <Switch
                            checked={company_permissions[perm.key]}
                            onCheckedChange={(event) => {
                                onUpdateCompanyPermissions(perm.key, event)
                            }}
                        />
                        <div>{perm.name}</div>
                    </div>
                }
            )}
        </div>
    </div>
}


function ProjectUserNotPartof({data, setData}: UserMgmtProps) {
    const {accessToken} = useAuthInfo();

    const onAddUserToProject = (project_id: string) => {
        if (!data) {
            return;
        }
        const body = {
            edit_project: true,
            project_id: project_id,
            user_email: data.user.email,
        }
        postWithToken(`/projects/${project_id}/add_user`,
            JSON.stringify(body),
            accessToken as string)
            .then((res) => {
                console.log(res)
                setData(res)
            }).catch((err) => {

        })
    }

    const card_columns: ColumnDef<ProjectMeta>[] = [
        {
            header: 'Name',
            accessorKey: 'name',
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({row}: any) => {
                const project_id = row.original.project_id;
                return (
                    <Button
                        variant={'outline'}
                        onClick={() => onAddUserToProject(project_id)}>Add Project
                    </Button>)
            }
        },
    ]
    return <>
        Add user to projects:
        <DataTable columns={card_columns} data={data.projects}/>
    </>
}


function UserManagementView(props: WithLoggedInAuthInfoProps) {
    const {user_id} = useParams<string>();
    const userContext = useContext(UserContext);
    const ctype = userContext?.company_type as CompanyType;
    const [data, setData] = useState<UserManagementView | null>(null)

    const {toast} = useToast()

    useEffect(() => {
        fetchWithToken(`/user/manage/${user_id}/permissions`, props.accessToken)
            .then((x) => {
                console.log(x)
                setData(x)
            })
    }, [user_id])

    const onChangeUserPermission = (project_id: string, index: number, permission: string, value: number) => {
        if (!data) {
            return;
        }
        // @ts-ignore
        data.user_projects[index][permission] = value;
        // @ts-ignore
        data.user_projects[index].project_id = project_id;
        patchWithToken(`/user/manage/${user_id}/project_permissions`,
            JSON.stringify([data.user_projects[index]]), props.accessToken)
            .then((x) => {
                setData(x)

            }).catch((err) => {
            toast({
                title: "Error",
                description: "Error updating permissions: " + String(err),
                variant: "destructive",
            });
        })
    }

    const onRemoveUserToProject = (project_id: string) => {
        if(!data){ return ;}
        deleteWithToken(`/projects/${project_id}/remove_user/${user_id}`, props.accessToken)
            .then((x) => {
                let updated = {...data};
                let project = updated.user_projects.find(
                    (p) => p.project_meta.project_id === project_id)

                updated.user_projects = updated.user_projects.filter(
                    (p) => p.project_meta.project_id !== project_id)
                // updated.projects = [updated.projects] && project
                setData(updated)
            }).catch((x)=> {

        })

    }

    const card_Existing_columns: ColumnDef<PermissionedDirectoryEntryRef>[] = [
        {
            header: 'Name',
            accessorKey: 'project_meta.name',
        },
        {
            header: 'VE',
            accessorKey: 'value_engineering',
            cell: ({row}: any) => <PermissionDropdown
                row={row}
                onChange={onChangeUserPermission}
                key_str={'value_engineering'}
                max={ctype === CompanyType.developer ? 4 : 4}/>
        },
        {
            header: 'Bidding',
            accessorKey: 'bidding',
            cell: ({row}: any) => <PermissionDropdown
                row={row}
                onChange={onChangeUserPermission}
                key_str={'bidding'}
                max={ctype === CompanyType.developer ? 2 : 4}/>
        },
        {
            header: 'Directory',
            accessorKey: 'directory',
            cell: ({row}: any) => <PermissionDropdown
                row={row}
                onChange={onChangeUserPermission}
                key_str={'directory'}
                max={ctype === CompanyType.developer ? 8 : 2}/>
        },
        {
            header: 'Project',
            accessorKey: 'project',
            cell: ({row}: any) => <PermissionDropdown
                row={row}
                onChange={onChangeUserPermission}
                key_str={'project'}
                max={ctype === CompanyType.developer ? 8 : 2}/>
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({row}: any) => {
                const project_id = row.original.project_meta.project_id
                return <Button variant={'outline'}
                               onClick={() => onRemoveUserToProject(project_id)}>Remove from Project
                </Button>

            }
        },
    ]

    const resendInvite = () => {
        postWithToken(
            `/company/${userContext?.company_id}/resend_invite`,
            JSON.stringify({email: user_id, name: '', projects: []}),
            props.accessToken
        ).then((data) => {
            console.log(data)
            if (data.status === 200) {
                toast({
                    title: "Success!",
                    description: "Invite send. ",
                    variant: "default",
                });
            } else {
                toast({
                    title: "Error",
                    description: "error sending invite ",
                    variant: "destructive",
                });
            }
        }).catch((err) => {
                toast({
                    title: "Error",
                    description: "error sending invite ",
                    variant: "destructive",
                });
            }
        )
    }

    if (!data) {
        return <></>
    }

    return (
        <div>
            <h1>User Management</h1>
            <Card>
                <CardHeader className="w-full columns-2  justify-between">
                    <CardTitle>User:{data.user.name} | {data.user.email}</CardTitle>
                    <div>
                        <Button onClick={resendInvite}>Resend invite</Button>
                        <Button>Disable User</Button>
                    </div>
                </CardHeader>
                <CardContent>
                    Profile Info:
                    {/*<UserProfile />*/}
                    <br/>
                    <Separator/>
                    <CompanyPermissionView data={data} setData={setData}/>
                    <br/>
                    <Separator/>
                    Add user to projects:
                    <ProjectUserNotPartof data={data} setData={setData}/>
                    <br/>
                    Projects user is a member of:

                    <br/>
                    <DataTable columns={card_Existing_columns} data={data.user_projects}/>
                </CardContent>
            </Card>
        </div>
    )
}

export default withRequiredAuthInfo(UserManagementView);
